import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Loader2 } from "lucide-react";

type DigestRow = Record<string, unknown> & { occurred_at: string };
type Digest = {
  range: { from: string; to: string };
  totals: Record<string, number>;
  users_created: DigestRow[];
  users_deleted: DigestRow[];
  subscriptions_created: DigestRow[];
  subscriptions_renewed: DigestRow[];
  subscriptions_cancelled: DigestRow[];
  subscriptions_refunded: DigestRow[];
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });

export default function AccountEventsPage() {
  const [hours, setHours] = useState<number>(24);
  const [appliedHours, setAppliedHours] = useState<number>(24);

  const { data, isLoading, error, refetch, isFetching } = useQuery<Digest>({
    queryKey: ["account-events-digest", appliedHours],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke(
        "account-changes-digest",
        { method: "GET", body: undefined, headers: {} as never },
      );
      // supabase-js doesn't pass query params; build URL manually
      if (data || error) {
        // ignore - use fetch fallback below
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/account-changes-digest?hours=${appliedHours}`;
      const session = (await supabase.auth.getSession()).data.session;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      return (await res.json()) as Digest;
    },
  });

  const renderTable = (rows: DigestRow[], cols: { key: string; label: string }[]) => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Quando</TableHead>
            {cols.map((c) => (
              <TableHead key={c.key}>{c.label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={cols.length + 1} className="text-center text-muted-foreground py-8">
                Nenhum evento no período
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="whitespace-nowrap">{fmt(r.occurred_at)}</TableCell>
                {cols.map((c) => (
                  <TableCell key={c.key}>{String(r[c.key] ?? "—")}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Eventos de Conta</h1>
        <p className="text-muted-foreground">
          Histórico de criação de usuários e mudanças em assinaturas.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Período</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="space-y-2">
            <Label htmlFor="hours">Últimas (horas)</Label>
            <Input
              id="hours"
              type="number"
              min={1}
              max={720}
              value={hours}
              onChange={(e) => setHours(Number(e.target.value) || 24)}
              className="w-32"
            />
          </div>
          <Button
            onClick={() => {
              setAppliedHours(hours);
              refetch();
            }}
            disabled={isFetching}
          >
            {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Atualizar
          </Button>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="pt-6 text-destructive">
            Erro: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {Object.entries(data.totals).map(([k, v]) => (
              <Card key={k}>
                <CardContent className="pt-6">
                  <div className="text-xs text-muted-foreground">{k}</div>
                  <div className="text-2xl font-bold">{v}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="users_created">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="users_created">Usuários criados ({data.totals.users_created})</TabsTrigger>
              <TabsTrigger value="users_deleted">Usuários removidos ({data.totals.users_deleted})</TabsTrigger>
              <TabsTrigger value="subscriptions_created">Assinaturas novas ({data.totals.subscriptions_created})</TabsTrigger>
              <TabsTrigger value="subscriptions_renewed">Renovações ({data.totals.subscriptions_renewed})</TabsTrigger>
              <TabsTrigger value="subscriptions_cancelled">Cancelamentos ({data.totals.subscriptions_cancelled})</TabsTrigger>
              <TabsTrigger value="subscriptions_refunded">Reembolsos ({data.totals.subscriptions_refunded})</TabsTrigger>
            </TabsList>

            <TabsContent value="users_created" className="mt-4">
              {renderTable(data.users_created, [
                { key: "email", label: "Email" },
                { key: "name", label: "Nome" },
                { key: "username", label: "Username" },
              ])}
            </TabsContent>
            <TabsContent value="users_deleted" className="mt-4">
              {renderTable(data.users_deleted, [
                { key: "email", label: "Email" },
                { key: "name", label: "Nome" },
                { key: "username", label: "Username" },
              ])}
            </TabsContent>
            <TabsContent value="subscriptions_created" className="mt-4">
              {renderTable(data.subscriptions_created, [
                { key: "profile_id", label: "Profile" },
                { key: "plan_id", label: "Plano" },
                { key: "store", label: "Store" },
                { key: "expires_at", label: "Expira em" },
              ])}
            </TabsContent>
            <TabsContent value="subscriptions_renewed" className="mt-4">
              {renderTable(data.subscriptions_renewed, [
                { key: "profile_id", label: "Profile" },
                { key: "plan_id", label: "Plano" },
                { key: "store", label: "Store" },
                { key: "expires_at", label: "Nova expiração" },
              ])}
            </TabsContent>
            <TabsContent value="subscriptions_cancelled" className="mt-4">
              {renderTable(data.subscriptions_cancelled, [
                { key: "profile_id", label: "Profile" },
                { key: "plan_id", label: "Plano" },
                { key: "store", label: "Store" },
                { key: "expires_at", label: "Expira em" },
              ])}
            </TabsContent>
            <TabsContent value="subscriptions_refunded" className="mt-4">
              {renderTable(data.subscriptions_refunded, [
                { key: "profile_id", label: "Profile" },
                { key: "plan_id", label: "Plano" },
                { key: "store", label: "Store" },
              ])}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
